import { z } from 'zod'

import { appointmentTypeIdSchema } from '../../entities/appointment-type'
import { clinicIdSchema } from '../../entities/clinic'
import { fileKeySchema } from '../../entities/file'
import { invoiceSchema } from '../../entities/invoice'
import { noteTypeSchema } from '../../entities/note'
import {
  dateOfBirthSchema,
  PATIENT_PHOTO_CONTENT_TYPES,
  patientSchema,
} from '../../entities/patient'
import { regionIdSchema } from '../../entities/region'
import { staffMemberSchema } from '../../entities/staff'
import {
  type SubscriptionUpgradePreview,
  subscriptionPlanSchema,
  subscriptionSchema,
} from '../../entities/subscription'
import { procedureTypeSchema, taskStatusSchema } from '../../entities/task'
import { vitalsSchema } from '../../entities/vitals'
import { isoDateTime } from '../../lib/date'
import { fileResponseSchema } from './file'
import {
  appointmentWithRelationsSchema,
  communicationWithPatientSchema,
  consultationWithRelationsSchema,
  noteWithStaffSchema,
  patientWithOwnersSchema,
} from './relations'
import { taskResponseSchema } from './tasks'
import { treatmentWithContextSchema } from './treatments'

// ---------------------------------------------------------------------------
// GET /api/patients
// ---------------------------------------------------------------------------

export const getPatientsQuerySchema = z.object({
  q: z
    .string()
    .optional()
    .transform((v) => (v && v.length >= 2 ? v : undefined)),
  limit: z.coerce.number<number>().int().min(1).max(200).optional(),
  offset: z.coerce.number<number>().int().min(0).optional(),
  // Optional filter on the owner's region: omit to list patients across all
  // regions. No default — absence deliberately means "all regions".
  region: regionIdSchema.optional(),
  // When true, archived patients are included in the results. Defaults to false.
  includeArchived: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .optional()
    .transform((v) => v === true || v === 'true'),
})
export type GetPatientsQuery = z.infer<typeof getPatientsQuerySchema>

export const getPatientsResponseSchema = z.object({
  patients: z.array(patientWithOwnersSchema),
  totalCount: z.number().int(),
})

export const getPatientSummaryResponseSchema = z.object({
  summary: z
    .object({
      id: z.uuid(),
      content: z.string(),
      requestedAt: isoDateTime,
      /** A newer summary is still being generated. */
      stale: z.boolean(),
    })
    .nullable(),
})

// ---------------------------------------------------------------------------
// GET /api/patients/:patientId
// ---------------------------------------------------------------------------

export const getPatientResponseSchema = z.object({
  patient: patientWithOwnersSchema.extend({
    /** Short-lived presigned URL for the patient's photo. */
    photoUrl: z.string().nullable(),
    owner: patientWithOwnersSchema.shape.owner.extend({
      /** The owner's other pets. */
      pets: z.array(
        patientSchema.pick({ id: true, name: true, species: true, sex: true, archivedAt: true })
      ),
    }),
  }),
})

export type GetPatientResponse = z.output<typeof getPatientResponseSchema>

// ---------------------------------------------------------------------------
// GET /api/patients/:patientId/timeline
// ---------------------------------------------------------------------------

export const TIMELINE_ENTRY_TYPES = [
  'appointment',
  'communication',
  'consultation',
  'note',
  'task',
  'treatment',
  'vitals',
] as const
export const timelineEntryTypeSchema = z.enum(TIMELINE_ENTRY_TYPES)
export type TimelineEntryType = z.infer<typeof timelineEntryTypeSchema>

export const getPatientTimelineQuerySchema = z.object({
  /** Entry types to include; all when omitted. */
  types: z.array(timelineEntryTypeSchema).min(1).optional(),
})

const timelineEntrySchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('consultation'),
    date: isoDateTime,
    consultation: consultationWithRelationsSchema,
  }),
  z.object({ type: z.literal('note'), date: isoDateTime, note: noteWithStaffSchema }),
  z.object({ type: z.literal('vitals'), date: isoDateTime, vitals: vitalsSchema }),
  z.object({
    type: z.literal('appointment'),
    date: isoDateTime,
    appointment: appointmentWithRelationsSchema,
  }),
  z.object({
    type: z.literal('treatment'),
    date: isoDateTime,
    treatment: treatmentWithContextSchema,
  }),
  z.object({ type: z.literal('task'), date: isoDateTime, task: taskResponseSchema }),
  z.object({
    type: z.literal('communication'),
    date: isoDateTime,
    communication: communicationWithPatientSchema,
  }),
])
export type TimelineEntry = z.output<typeof timelineEntrySchema>

/** Newest first; upcoming appointments lead. */
export const getPatientTimelineResponseSchema = z.object({
  entries: z.array(timelineEntrySchema),
})

// ---------------------------------------------------------------------------
// GET /api/patients/:patientId/appointment-types
// ---------------------------------------------------------------------------

export const getPatientAppointmentTypesResponseSchema = z.object({
  appointmentTypeIds: z.array(appointmentTypeIdSchema),
})

// ---------------------------------------------------------------------------
// GET /api/patients/:patientId/subscription
// ---------------------------------------------------------------------------

/** A chargeable procedure fee for a patient, as computed from their subscription and species. */
export const patientProcedureFeeSchema = z.object({
  procedure: procedureTypeSchema,
  amount: z.number().int(),
})

export type PatientProcedureFee = z.output<typeof patientProcedureFeeSchema>

const subscriptionIdentifiers = {
  stripeSubscriptionId: z.string(),
  stripeSubscriptionUrl: z.string(),
}

/**
 * The full subscription first: a union keeps the first branch that parses, and the
 * shorter branches would strip its details. `status` is absent on the others so callers
 * can narrow on it.
 */
export const getPatientSubscriptionResponseSchema = z.union([
  subscriptionSchema.extend({
    ...subscriptionIdentifiers,
    matchesMemberStripeCustomerId: z.boolean(),
    procedureFees: z.array(patientProcedureFeeSchema),
  }),
  /** Stripe was unreachable, so only the stored identifiers are known. */
  z.object({
    ...subscriptionIdentifiers,
    status: z.never().optional(),
    procedureFees: z.array(patientProcedureFeeSchema),
  }),
  z.object({
    stripeSubscriptionId: z.null(),
    stripeSubscriptionUrl: z.null(),
    status: z.never().optional(),
  }),
])

export type GetPatientSubscriptionResponse = z.output<typeof getPatientSubscriptionResponseSchema>

// ---------------------------------------------------------------------------
// POST /api/members/:memberId/patients
// ---------------------------------------------------------------------------

export const postPatientBodySchema = z.object({
  name: z.string().min(1),
  species: z.string().min(1),
  sex: z.enum(['male', 'female']),
  // breed and dateOfBirth are required on creation
  breed: z.string().min(1),
  dateOfBirth: dateOfBirthSchema,
  // color is an optional descriptive attribute (e.g. "Black & tan") — may be omitted on create
  color: z.string().nullish(),
  isNeutered: z.boolean().nullable(),
  microchipNumber: z.string().nullable(),
  priceId: z.string().min(1).nullable(),
})
export type PostPatientBody = z.infer<typeof postPatientBodySchema>

export const patientPhotoSchema = fileKeySchema.extend({
  contentType: z.enum(PATIENT_PHOTO_CONTENT_TYPES, {
    message: 'Photo must be a JPEG, PNG or WebP',
  }),
})
export type PatientPhoto = z.infer<typeof patientPhotoSchema>

// ---------------------------------------------------------------------------
// PATCH /api/patients/:patientId
// ---------------------------------------------------------------------------

// breed and dateOfBirth are required on create but only optional here - deliberately loose
// on edit so staff can update unrelated fields (e.g. microchip number) without being blocked
// by legacy patients whose breed/DOB never made it through. They're optional but explicitly
// not nullable: an edit can omit them to leave the stored value alone, but cannot actively
// clear an existing value back to null. The update form mirrors this by blocking clears at
// the field level once a value is on record.
export const patchPatientBodySchema = z.object({
  ...postPatientBodySchema.partial().shape,
  // .optional() only - explicit null is rejected (see comment above)
  breed: z.string().min(1).optional(),
  dateOfBirth: dateOfBirthSchema.optional(),
  // These remain nullish: clearing is a legitimate edit (e.g. unlink a Stripe subscription,
  // remove an internal note).
  stripeSubscriptionId: z.string().nullish(),
  internalNotes: z.string().nullish(),
  // A file uploaded via /files/upload-url; null removes the photo.
  photo: patientPhotoSchema.nullish(),
})
export type PatchPatientBody = z.infer<typeof patchPatientBodySchema>

// ---------------------------------------------------------------------------
// PATCH /api/patients/:patientId/subscription
// ---------------------------------------------------------------------------

export const patchSubscriptionPaymentMethodBodySchema = z.object({
  paymentMethodId: z.string().min(1),
})
export type PatchSubscriptionPaymentMethodBody = z.infer<
  typeof patchSubscriptionPaymentMethodBodySchema
>

export const patchPatientSubscriptionBodySchema = z.object({
  // Stripe price IDs — replaces current addon items on the subscription
  addons: z.array(z.string()),
})
export type PatchPatientSubscriptionBody = z.infer<typeof patchPatientSubscriptionBodySchema>

export const patchPatientSubscriptionResponseSchema =
  subscriptionSchema.extend(subscriptionIdentifiers)

export const getPatientInvoicesResponseSchema = z.object({
  /** Membership invoices and the penalty fees charged for the patient's appointments, newest first. */
  invoices: z.array(invoiceSchema),
})
export type GetPatientInvoicesResponse = z.output<typeof getPatientInvoicesResponseSchema>

// ---------------------------------------------------------------------------
// POST /api/patients/:patientId/invoices/:invoiceId/send-payment-link
// ---------------------------------------------------------------------------

// Optional recipient override. The client pre-fills the member's email but staff may
// send the link elsewhere; omitted → the server falls back to the member's email.
export const postSendInvoicePaymentLinkBodySchema = z.object({
  email: z.email().optional(),
})
export type PostSendInvoicePaymentLinkBody = z.infer<typeof postSendInvoicePaymentLinkBodySchema>

// Emails a Stripe hosted payment link for an overdue invoice; `email` is the address it was sent to.
export const postSendInvoicePaymentLinkResponseSchema = z.object({
  sent: z.literal(true),
  email: z.string(),
})

// ---------------------------------------------------------------------------
// POST /api/patients/:patientId/booking-link — generate a customer booking URL
// ---------------------------------------------------------------------------

export type PostPatientBookingLinkResponse = { url: string }

// ---------------------------------------------------------------------------
// POST /api/patients/:patientId/archive — archive a patient with a reason
// DELETE /api/patients/:patientId/archive — restore an archived patient
// ---------------------------------------------------------------------------

export const postPatientArchiveBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
})
export type PostPatientArchiveBody = z.infer<typeof postPatientArchiveBodySchema>

// GET /api/patients/:patientId/files — all files attached to a patient.
//
// Files attach to a patient through two sources:
//   - notes  → note_files (rich clinical context: note type/content + consultation/appointment)
//   - tasks  → task_files (task title + status)
//
// The response carries enough context to render each file without a follow-up
// fetch (see PatientFilesCard / patient files dialog).

/** Consultation context surfaced for a note file linked to a consultation. */
const patientFileConsultationSchema = z.object({
  id: z.uuid(),
  appointment: z
    .object({
      id: z.uuid(),
      /** Resolved from the static APPOINTMENT_TYPES config, not a DB relation. */
      appointmentTypeName: z.string(),
      startTime: isoDateTime,
      /** Needed to render `startTime` in the clinic's own timezone. Null for remote. */
      clinicId: clinicIdSchema.nullable(),
    })
    .nullable(),
})

const patientFileSourceSchema = z.discriminatedUnion('type', [
  /** A file attached via a clinical note. */
  z.object({
    type: z.literal('note'),
    noteId: z.uuid(),
    noteType: noteTypeSchema,
    /** Plain-text snippet of the note content, for at-a-glance context. */
    contentPreview: z.string().nullable(),
    consultation: patientFileConsultationSchema.nullable(),
  }),
  /** A file attached via a task. */
  z.object({
    type: z.literal('task'),
    taskId: z.uuid(),
    taskTitle: z.string(),
    taskStatus: taskStatusSchema,
  }),
])

export type PatientFileSource = z.output<typeof patientFileSourceSchema>
export type PatientFileNoteSource = Extract<PatientFileSource, { type: 'note' }>
export type PatientFileTaskSource = Extract<PatientFileSource, { type: 'task' }>

/** A single file belonging to a patient, with its originating context. */
export const patientFileSchema = fileResponseSchema.extend({
  createdAt: isoDateTime,
  uploadedByStaff: staffMemberSchema.pick({ id: true, firstName: true, lastName: true }).nullable(),
  source: patientFileSourceSchema,
})

export type PatientFile = z.output<typeof patientFileSchema>

// ---------------------------------------------------------------------------
// GET /api/patients/:patientId/subscription/upgrade
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// POST /api/patients/:patientId/subscription/upgrade
// ---------------------------------------------------------------------------

// No `paymentMethodId`: Stripe charges the member's saved card off-session.
// A failed charge leaves the upgrade pending until the member pays its invoice.
export type GetPatientSubscriptionUpgradeResponse = SubscriptionUpgradePreview

export const postPatientSubscriptionUpgradeBodySchema = z.object({
  plan: subscriptionPlanSchema,
  attemptToken: z.uuid(),
})
export type PostPatientSubscriptionUpgradeBody = z.infer<
  typeof postPatientSubscriptionUpgradeBodySchema
>
